import React from 'react';
import {
  AreaChart as ReAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface DataPoint {
  [key: string]: string | number;
}

interface AreaChartProps {
  data: DataPoint[];
  xKey: string;
  yKey: string;
  color?: string;
  fillColor?: string;
  yLabel?: string;
  unit?: string;
  yDomain?: [number | 'auto', number | 'auto'];
  height?: number;
  tickInterval?: number;
}

export default function AreaChart({
  data,
  xKey,
  yKey,
  color = '#2472C8',
  fillColor,
  yLabel,
  unit = '',
  yDomain,
  height = 240,
  tickInterval,
}: AreaChartProps) {
  const fill = fillColor ?? color;

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-text-tertiary text-sm">
        No data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ReAreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id={`grad-${yKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={fill} stopOpacity={0.3} />
            <stop offset="95%" stopColor={fill} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 11, fill: '#999999' }}
          axisLine={false}
          tickLine={false}
          interval={tickInterval ?? 'preserveStartEnd'}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#999999' }}
          axisLine={false}
          tickLine={false}
          domain={yDomain}
          label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11, fill: '#999999' } } : undefined}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #DDDDDD' }}
          formatter={(val: unknown) => [`${val}${unit}`]}
        />
        <Area
          type="monotone"
          dataKey={yKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#grad-${yKey})`}
          dot={false}
          activeDot={{ r: 4, fill: color }}
        />
      </ReAreaChart>
    </ResponsiveContainer>
  );
}
